import { useMemo, useState } from 'react';
import {
  GlobalButton,
  SearchInput,
  SelectInput,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
} from '@mui/icons-material';
import type { SelectInputOption } from '@/components/inputs/SelectInput';

import { ServiceVisitTypeFilters, VisitType, VisitTypeLabels } from '../models';
import { useLogisticOperatorOptions } from '../hooks';

interface Props {
  initialFilter: ServiceVisitTypeFilters;
  filter: ServiceVisitTypeFilters;
  search: (filter: ServiceVisitTypeFilters) => void;
}

const typeOptions: SelectInputOption[] = [
  { value: '', label: 'Todos los tipos' },
  { value: VisitType.DELIVERY, label: VisitTypeLabels[VisitType.DELIVERY] },
  { value: VisitType.PICKUP, label: VisitTypeLabels[VisitType.PICKUP] },
];

const ServiceVisitTypesSearchBar = ({
  initialFilter,
  filter,
  search,
}: Props) => {
  const [localFilter, setLocalFilter] =
    useState<ServiceVisitTypeFilters>(filter);
  const { options: operatorOptions, isLoading: isLoadingOperators } =
    useLogisticOperatorOptions();

  const operatorSelectOptions: SelectInputOption[] = useMemo(
    () => [
      { value: '', label: 'Todos los operadores' },
      ...operatorOptions,
    ],
    [operatorOptions]
  );

  const resetFilters = () => {
    setLocalFilter(initialFilter);
    search(initialFilter);
  };

  const handleSearch = () => search(localFilter);

  return (
    <div className='w-full p-5 flex flex-col justify-center bg-white rounded-lg shadow-md gap-5'>
      <div className='flex flex-col gap-2.5 md:flex-row'>
        <SearchInput
          name='service-visit-type-name-search'
          placeholder='Nombre'
          value={localFilter.name || ''}
          onChange={(value) =>
            setLocalFilter((prev) => ({ ...prev, name: value || undefined }))
          }
        />
        <SelectInput
          name='service-visit-type-type-filter'
          placeholder='Filtrar por tipo'
          options={typeOptions}
          value={localFilter.type || ''}
          onChange={(value) =>
            setLocalFilter((prev) => ({
              ...prev,
              type: value ? (value as VisitType) : undefined,
            }))
          }
        />
        <SelectInput
          name='service-visit-type-operator-filter'
          placeholder='Filtrar por operador'
          options={operatorSelectOptions}
          value={localFilter.logisticOperatorId || ''}
          disabled={isLoadingOperators}
          onChange={(value) =>
            setLocalFilter((prev) => ({
              ...prev,
              logisticOperatorId: value || undefined,
            }))
          }
        />
      </div>
      <div className='flex gap-2 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilters}
        >
          <TrashIcon />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSearch}
        >
          <SearchIcon />
          Buscar
        </GlobalButton>
      </div>
    </div>
  );
};

export default ServiceVisitTypesSearchBar;
